import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Notebook, BookOpen, SignOut, ChartBar, Gear, Warning, X, Palette, CurrencyDollar, Compass as CompassIcon, Leaf, ClipboardText, List, Calculator, TestTube, UsersThree, Plus, CaretDown, AddressBook } from "@phosphor-icons/react";

const BETA_BANNER_KEY = "alaine_beta_banner_dismissed_v1";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [showBanner, setShowBanner] = useState(false);

  // Radix dialogs and menus mount under body rather than inside the layout.
  useEffect(() => {
    document.body.dataset.benchWorkspace = "true";
    return () => { delete document.body.dataset.benchWorkspace; };
  }, []);

  useEffect(() => {
    try { setShowBanner(sessionStorage.getItem(BETA_BANNER_KEY) !== "1"); }
    catch { setShowBanner(true); }
  }, []);

  const dismissBanner = () => {
    try { sessionStorage.setItem(BETA_BANNER_KEY, "1"); } catch (e) { /* ignore */ }
    setShowBanner(false);
  };

  const isDemo = user?.role === "demo" || user?.is_demo;
  const crmOnly = user?.role === "crm_staff";
  const groups = crmOnly ? [{ label: "Clients", items: [{ to: "/clients", label: "Clients & Pipeline", icon: AddressBook }] }] : [
    { label: "Projects & formulation", items: [
      { to: "/dashboard", label: "Formulas", icon: ChartBar, match: ["/dashboard", "/product", "/rd", "/builder"] },
      { to: "/research/scope", label: "Project Scope", icon: ClipboardText },
      { to: "/applications", label: "Applications", icon: Calculator },
    ] },
    { label: "Ingredients & suppliers", items: [
      { to: "/ingredients", label: "Ingredients & Supplier Specs", icon: BookOpen },
    ] },
    { label: "Testing & quality", items: [
      { to: "/research/tests", label: "Analytical Tests", icon: TestTube },
      { to: "/research/sensory", label: "Sensory Evaluations", icon: UsersThree },
    ] },
    ...(user?.role === "admin" ? [{ label: "Clients", items: [
      { to: "/clients", label: "Clients & Pipeline", icon: AddressBook },
    ] }] : []),
  ];
  const tools = crmOnly ? [] : [
    { to: "/notes", label: "Regulations", icon: Notebook },
    { to: "/organic", label: "Organic", icon: Leaf },
    { to: "/compass", label: "Alaine's Compass", icon: CompassIcon },
    { to: "/hue", label: "Alaine's Hue", icon: Palette, badge: "Soon" },
    { to: "/pricing", label: "Pricing", icon: CurrencyDollar },
    ...(isDemo ? [] : [{ to: "/settings", label: "Settings", icon: Gear }]),
    ...(user?.role === "admin" ? [{ to: "/admin/technical-reviews", label: "Tech Reviews", icon: ClipboardText }] : []),
  ];
  const [menuOpen, setMenuOpen] = useState(false);
  const toolsActive = tools.some((t) => loc.pathname === t.to || loc.pathname.startsWith(t.to + "/"));
  const [toolsOpen, setToolsOpen] = useState(toolsActive);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [menuOpen]);
  const isActive = (item) => (item.match || [item.to]).some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));

  const NavItem = ({ item }) => {
    const Icon = item.icon; const active = isActive(item);
    return (
      <Link to={item.to} aria-current={active ? "page" : undefined}
        data-testid={`nav-${item.to.replace(/\//g, "-").slice(1)}`}
        className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${active ? "bg-[#EAF0F6] text-[#17324D] font-semibold ring-1 ring-inset ring-[#C5D3E2] shadow-[inset_3px_0_0_#C69A4B]" : "text-[#5B6570] hover:bg-stone-100"}`}>
        <Icon size={17} weight={active ? "fill" : "regular"} className="shrink-0" />
        <span className="flex-1 leading-tight">{item.label}</span>
        {item.badge && <span className="text-[9px] uppercase tracking-wider font-bold bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">{item.badge}</span>}
      </Link>
    );
  };

  const navBody = (
    <nav aria-label="Main" className="flex flex-col gap-5 p-3">
      {!crmOnly && <Link to="/builder" data-testid="nav-new-formula"
        className="flex items-center justify-center gap-2 rounded-md bg-[#17324D] hover:bg-[#22476B] text-white text-sm font-semibold px-3 py-2">
        <Plus size={15} weight="bold" /> New formula
      </Link>}
      {groups.map((g) => (
        <div key={g.label}>
          <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.2em] text-[#5B6570] font-semibold">{g.label}</div>
          <div className="flex flex-col gap-0.5">{g.items.map((i) => <NavItem key={i.to} item={i} />)}</div>
        </div>
      ))}
      {!crmOnly && <div className="border-t border-stone-200 pt-3">
        <button type="button" onClick={() => setToolsOpen((o) => !o)} aria-expanded={toolsOpen} aria-controls="nav-tools"
          className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#5B6570] font-semibold hover:text-[#17324D]"
          data-testid="button-toggle-tools">
          Tools &amp; settings <CaretDown size={12} className={`transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
        </button>
        <div id="nav-tools" hidden={!toolsOpen} className={toolsOpen ? "flex flex-col gap-0.5 mt-1" : "hidden"}>{tools.map((i) => <NavItem key={i.to} item={i} />)}</div>
      </div>}
    </nav>
  );

  return (
    <div className="bench-workspace min-h-screen bg-stone-50 flex flex-col">
      {showBanner && !crmOnly && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-xs sm:text-[13px]" data-testid="beta-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-start sm:items-center gap-3">
            <Warning size={16} weight="fill" className="shrink-0 mt-0.5 sm:mt-0" />
            <div className="flex-1 leading-snug">
              <b className="uppercase tracking-wider text-[10px] sm:text-xs">FDA Compliance Notice</b>
              <span className="hidden sm:inline"> — </span>
              <span className="block sm:inline">
                Outputs are <b>calculations, not regulatory advice</b>. Independent laboratory analysis &amp; food-law attorney review required before commercial use.
              </span>
            </div>
            <button onClick={dismissBanner} className="shrink-0 hover:bg-amber-200 rounded-md p-1"
              data-testid="beta-banner-dismiss" aria-label="Dismiss">
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}
      <header className="border-b border-stone-200 bg-white sticky top-0 z-40">
        <div className="px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
          <button type="button" className="md:hidden p-2 -ml-2 rounded-md hover:bg-stone-100" aria-label="Open menu"
            aria-expanded={menuOpen} aria-controls="mobile-nav" onClick={() => setMenuOpen(true)} data-testid="button-mobile-menu">
            <List size={22} />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <div className="h-8 w-8 rounded-md bg-[#17324D] text-[#C69A4B] grid place-items-center font-bold">A</div>
            <div className="leading-none">
              <div className="font-bold text-lg tracking-tight whitespace-nowrap">Alaine's Bench<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup></div>
              <div className="hidden xl:block text-[10px] italic text-[#17324D] mt-0.5 whitespace-nowrap">Flavor development made simple</div>
              <div className="hidden xl:block text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5 whitespace-nowrap">Built on FDA Labeling Rules</div>
            </div>
          </Link>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {user === null ? null : user ? (
              <>
                <div className="hidden xl:block text-right">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-stone-500">{user.email}</div>
                </div>
                <Button variant="outline" size="sm" onClick={async () => {
                    await logout();
                    // Full reload to the landing page: in-app navigation races
                    // the route guards (Paywalled -> /login, ComingSoon ->
                    // /dashboard), so a hard redirect is the only reliable exit.
                    window.location.assign(`${process.env.PUBLIC_URL || ""}/`);
                  }}
                  data-testid="logout-button" className="gap-1.5 whitespace-nowrap">
                  <SignOut size={16} weight="bold" />
                  <span>Log out</span>
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => navigate("/login")}
                data-testid="login-button" className="bg-[#17324D] hover:bg-[#22476B] whitespace-nowrap">
                Log in
              </Button>
            )}
          </div>
        </div>
      </header>
      <div className="flex flex-1 min-h-0">
        <aside className="hidden md:block w-60 shrink-0 border-r border-stone-200 bg-white" data-testid="sidebar">
          <div className="sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto">{navBody}</div>
        </aside>
        {menuOpen && (
          <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" id="mobile-nav">
            <div className="absolute inset-0 bg-stone-900/40" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-xl overflow-y-auto">
              <div className="flex items-center justify-between px-4 h-14 border-b border-stone-200">
                <span className="font-bold tracking-tight">Alaine's Bench<sup className="text-[0.5em] ml-0.5">™</sup></span>
                <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="p-2 rounded-md hover:bg-stone-100" data-testid="button-close-mobile-menu"><X size={18} /></button>
              </div>
              {navBody}
            </div>
          </div>
        )}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <footer className="mt-12 border-t border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-stone-500 leading-relaxed">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="font-semibold text-stone-700">Liability Disclaimer</div>
            <div className="flex gap-3">
              <Link to="/trust" className="hover:text-[#17324D] underline" data-testid="footer-trust">Trust</Link>
              <Link to="/changelog" className="hover:text-[#17324D] underline" data-testid="footer-changelog">Changelog</Link>
              <Link to="/terms" className="hover:text-[#17324D] underline" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="hover:text-[#17324D] underline" data-testid="footer-privacy">Privacy</Link>
            </div>
          </div>
          Alaine's Bench is a calculation and formatting tool. Output is based on 21 CFR 101.9
          and FALCPA / FASTER Act guidance <b>applied to data you provide</b>. It is <b>not regulatory advice and not a
          substitute for laboratory analysis or review by a qualified food-law attorney</b>. The Compliance Assistant
          (AI) provides informational guidance only — never legal advice. Verify all values independently before any
          commercial distribution. By using this tool you accept our <Link to="/terms" className="underline">Terms</Link>.
          <div className="mt-2 pt-2 border-t border-stone-100 text-[11px] text-stone-400">
            © {new Date().getFullYear()} <b>Shirleys Daughter, LLC</b> · Alaine's Bench™ ·
            Confidential &amp; Proprietary · All rights reserved · Trademark Pending
          </div>
        </div>
      </footer>
    </div>
  );
}
